/* "Dataset at a glance": four averages as rings, then how the columns split by completeness and by
 * distinct values. The top of the Profile tab, as the design draws it.
 *
 * **An average nobody could measure is a hatched ring and a dash, never 0%.** An empty table has no
 * completeness to average, and a table profiled once has no stability to report; both say so in
 * words beside the ring. */

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type { ColumnDrift, ColumnSketch } from '@/shared/api/generated/catalog';
import { HelpLabel } from '@/shared/ui/InfoTip';

import { cardinalityLevel, profileAverages, share } from '../model/presentation';
import type { CardinalityLevel } from '../model/presentation';

type Tone = 'good' | 'watch' | 'risk' | 'teal' | 'none';

const FILL: Readonly<Record<Exclude<Tone, 'none'>, string>> = {
  good: 'var(--pf-fill-good)',
  watch: 'var(--pf-fill-watch)',
  risk: 'var(--pf-fill-risk)',
  teal: 'var(--pf-fill-teal)',
};

function bandTone(value: number | null): Tone {
  if (value === null) return 'none';
  if (value >= 0.9) return 'good';
  if (value >= 0.7) return 'watch';
  return 'risk';
}

/** The completeness bands, best first. */
const BANDS = ['good', 'watch', 'risk', 'none'] as const;
/** Distinct-value steps, most distinct first. */
const LEVELS: readonly CardinalityLevel[] = [5, 4, 3, 2, 1, 0];

export function GlanceSummary({
  columns,
  drift,
}: {
  readonly columns: readonly ColumnSketch[];
  readonly drift: readonly ColumnDrift[] | null;
}): ReactElement {
  const { t } = useTranslation();
  const averages = profileAverages(columns, drift);
  const total = Math.max(columns.length, 1);

  const completenessNote =
    averages.completeness === null
      ? 'nothing'
      : averages.completeness >= 0.9
        ? 'healthy'
        : averages.completeness >= 0.7
          ? 'watch'
          : 'atRisk';
  const uniquenessNote =
    averages.uniqueness === null
      ? 'nothing'
      : averages.uniqueness >= 0.5
        ? 'identifiers'
        : averages.uniqueness >= 0.05
          ? 'mix'
          : 'codes';
  const topNote =
    averages.topShare === null
      ? 'notRetained'
      : averages.topShare >= 0.9
        ? 'dominates'
        : averages.topShare >= 0.5
          ? 'few'
          : 'spread';

  const rings: readonly {
    readonly key: string;
    readonly value: number | null;
    readonly tone: Tone;
    readonly note: string;
    /** What the ring's figure means, behind an "i" beside its label; absent for a ring whose
     * label explains itself. */
    readonly help?: string;
  }[] = [
    {
      key: 'completeness',
      value: averages.completeness,
      tone: bandTone(averages.completeness),
      note: t(`profile.glance2.note.completeness.${completenessNote}`),
    },
    {
      key: 'uniqueness',
      value: averages.uniqueness,
      tone: averages.uniqueness === null ? 'none' : 'teal',
      note: t(`profile.glance2.note.uniqueness.${uniquenessNote}`),
    },
    {
      key: 'topShare',
      value: averages.topShare,
      tone:
        averages.topShare === null ? 'none' : averages.topShare >= 0.9 ? 'watch' : 'teal',
      note: t(`profile.glance2.note.topShare.${topNote}`),
      help: t('help.metric.topValue'),
    },
    {
      key: 'stable',
      value: averages.stable,
      tone: bandTone(averages.stable),
      note:
        averages.stable === null
          ? t('profile.glance2.note.stable.none')
          : t('profile.glance2.note.stable.changed', {
              changed: averages.changed,
              compared: averages.compared,
            }),
    },
  ];

  const bands: Record<(typeof BANDS)[number], number> = {
    good: 0,
    watch: 0,
    risk: 0,
    none: 0,
  };
  const levels: Record<CardinalityLevel, number> = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  for (const column of columns) {
    const band = bandTone(column.completeness);
    if (band !== 'teal') bands[band] += 1;
    levels[cardinalityLevel(column)] += 1;
  }

  return (
    <section className="lq-pp__glance2" aria-labelledby="profile-glance2-heading">
      <div className="lq-pp__glance2Head">
        <h3 id="profile-glance2-heading" className="lq-pp__cardTitle lq-pp__grow">
          {t('profile.glance2.title')}
        </h3>
        <span className="lq-pp__cardMeta">
          {t('profile.glance2.sub', { count: columns.length })}
        </span>
      </div>

      <div className="lq-pp__rings">
        {rings.map((ring) => {
          const filled = ring.value === null ? 0 : Math.max(1.5, ring.value * 100);
          return (
            <div key={ring.key} className="lq-pp__avg" data-tone={ring.tone}>
              <div
                className="lq-pp__avgRing"
                style={
                  ring.tone === 'none'
                    ? undefined
                    : {
                        background: `conic-gradient(${FILL[ring.tone]} 0 ${filled.toFixed(1)}%, var(--pf-track) ${filled.toFixed(1)}% 100%)`,
                      }
                }
              >
                <span className="lq-pp__avgFigure">
                  {ring.value === null ? '—' : share(ring.value)}
                </span>
              </div>
              <div className="lq-pp__avgText">
                <HelpLabel
                  subject={t(`profile.glance2.${ring.key}.label`)}
                  help={ring.help}
                >
                  <div className="lq-pp__avgLabel">
                    {t(`profile.glance2.${ring.key}.label`)}
                  </div>
                </HelpLabel>
                <div className="lq-pp__avgSub">
                  {t(`profile.glance2.${ring.key}.sub`)}
                </div>
                <div className="lq-pp__avgNote">{ring.note}</div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="lq-pp__splits">
        <div className="lq-pp__split">
          <div className="lq-pp__eyebrow">{t('profile.glance2.byCompleteness')}</div>
          <div className="lq-pp__splitBar" data-grow="">
            {BANDS.filter((band) => bands[band] > 0).map((band) => (
              <div
                key={band}
                className="lq-pp__splitPart"
                data-band={band}
                title={t('profile.glance2.partTitle', {
                  label: t(`profile.glance2.band.${band}`),
                  count: bands[band],
                })}
                style={{ width: `${((bands[band] / total) * 100).toFixed(2)}%` }}
              />
            ))}
          </div>
          <div className="lq-pp__splitKeys">
            {BANDS.filter((band) => bands[band] > 0).map((band) => (
              <span key={band} className="lq-pp__splitKey">
                <span className="lq-pp__splitDot" data-band={band} />
                {t(`profile.glance2.band.${band}`)}
                <strong>{bands[band]}</strong>
              </span>
            ))}
          </div>
        </div>
        <div className="lq-pp__split">
          <div className="lq-pp__eyebrow">{t('profile.glance2.byDistinct')}</div>
          <div className="lq-pp__splitBar" data-grow="">
            {LEVELS.filter((level) => levels[level] > 0).map((level) => (
              <div
                key={level}
                className="lq-pp__splitPart"
                data-level={level}
                title={t('profile.glance2.partTitle', {
                  label: t(`profile.glance2.level.${String(level)}`),
                  count: levels[level],
                })}
                style={{ width: `${((levels[level] / total) * 100).toFixed(2)}%` }}
              />
            ))}
          </div>
          <div className="lq-pp__splitKeys">
            {LEVELS.filter((level) => levels[level] > 0).map((level) => (
              <span key={level} className="lq-pp__splitKey">
                <span className="lq-pp__splitDot" data-level={level} />
                {t(`profile.glance2.level.${String(level)}`)}
                <strong>{levels[level]}</strong>
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
